<script setup lang="ts">
  import { computed } from 'vue';
  import { TagColor, TagFontWeight, TagAppearance, TagSize } from './Tag.types';
  import { Icon, IconSize, Typo, TypoVariant } from '@/components';

  interface TagProps {
    appearance?: `${TagAppearance}`;
    color?: `${TagColor}`;
    fontWeight?: `${TagFontWeight}`;
    size?: `${TagSize}`;
    iconLeft?: string;
    iconRight?: string;
  }

  const props = withDefaults(defineProps<TagProps>(), {
    appearance: TagAppearance.Primary,
    color: TagColor.Blue,
    fontWeight: TagFontWeight.Regular,
    size: TagSize.S,
    iconLeft: '',
    iconRight: '',
  });

  const iconSize = computed(() => {
    const sizeMap: Record<TagSize, IconSize> = {
      [TagSize.XS]: IconSize.XS,
      [TagSize.S]: IconSize.XS,
      [TagSize.M]: IconSize.S,
      [TagSize.L]: IconSize.M,
    };
    return sizeMap[props.size as TagSize] || IconSize.XS;
  });
  const isRegular = computed(() => props.fontWeight === TagFontWeight.Regular);

  const textVariant = computed(() => {
    const variants = {
      [TagSize.XS]: isRegular.value ? TypoVariant.BodyXSRegular : TypoVariant.BodyXSMedium,
      [TagSize.S]: isRegular.value ? TypoVariant.BodySRegular : TypoVariant.BodySMedium,
      [TagSize.M]: isRegular.value ? TypoVariant.BodyMRegular : TypoVariant.BodyMMedium,
      [TagSize.L]: isRegular.value ? TypoVariant.BodyLRegular : TypoVariant.BodyLMedium,
    };

    return variants[props.size] || TypoVariant.BodyXSRegular;
  });
</script>

<template>
  <span
    :class="[
      'tl-tag',
      `tl-tag_${props.appearance}`,
      `tl-tag_color-${props.color}`,
      `tl-tag_weight-${props.fontWeight}`,
    ]"
  >
    <Icon v-if="iconLeft" class="tl-tag__icon" :name="iconLeft" :size="iconSize" />
    <Typo :variant="textVariant">
      <slot />
    </Typo>
    <Icon v-if="iconRight" class="tl-tag__icon" :name="iconRight" :size="iconSize" />
  </span>
</template>

<style lang="scss" scoped>
  .tl-tag {
    $root: &;

    align-items: center;
    border-radius: var(--rounding-xs);
    box-sizing: border-box;
    display: inline-flex;
    gap: var(--spacing-xxs);
    padding: var(--spacing-xxxs) var(--spacing-xxs);

    &__icon {
      flex-shrink: 0;
      margin-top: 1px;
    }

    &_color {
      &-green {
        &#{$root}_primary {
          background-color: var(--color-green-50);
          color: var(--color-text-inverse);

          #{$root}__icon {
            fill: var(--color-text-inverse);
          }
        }

        &#{$root}_secondary {
          background-color: transparent;
          color: var(--color-green-50);

          #{$root}__icon {
            fill: var(--color-green-50);
          }

          &#{$root}_weight-medium {
            background-color: var(--color-green-05);
            color: var(--color-green-60);

            #{$root}__icon {
              fill: var(--color-green-60);
            }
          }
        }

        &#{$root}_tertiary {
          background-color: transparent;
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }

          &#{$root}_weight-medium {
            background-color: var(--color-green-05);
          }
        }
      }

      &-red {
        &#{$root}_primary {
          background-color: var(--color-rose-40);
          color: var(--color-text-inverse);

          #{$root}__icon {
            fill: var(--color-text-inverse);
          }
        }

        &#{$root}_secondary {
          background-color: transparent;
          color: var(--color-rose-40);

          #{$root}__icon {
            fill: var(--color-rose-40);
          }

          &#{$root}_weight-medium {
            background-color: var(--color-rose-05);
            color: var(--color-rose-60);

            #{$root}__icon {
              fill: var(--color-rose-60);
            }
          }
        }

        &#{$root}_tertiary {
          background-color: transparent;
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }

          &#{$root}_weight-medium {
            background-color: var(--color-rose-025);
          }
        }
      }

      &-orange {
        &#{$root}_primary {
          background-color: var(--color-orange-50);
          color: var(--color-text-inverse);

          #{$root}__icon {
            fill: var(--color-text-inverse);
          }
        }

        &#{$root}_secondary {
          background-color: transparent;
          color: var(--color-orange-50);

          #{$root}__icon {
            fill: var(--color-orange-50);
          }

          &#{$root}_weight-medium {
            background-color: var(--color-orange-05);
            color: var(--color-orange-80);

            #{$root}__icon {
              fill: var(--color-orange-80);
            }
          }
        }

        &#{$root}_tertiary {
          background-color: var(--color-orange-05);
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }
        }
      }

      &-blue {
        &#{$root}_primary {
          background-color: var(--color-sky-50);
          color: var(--color-text-inverse);

          #{$root}__icon {
            fill: var(--color-text-inverse);
          }
        }

        &#{$root}_secondary {
          background-color: var(--color-sky-05);
          color: var(--color-sky-60);

          #{$root}__icon {
            fill: var(--color-sky-60);
          }

          &#{$root}_weight-medium {
            background-color: var(--color-sky-10);
          }
        }

        &#{$root}_tertiary {
          background-color: var(--color-sky-05);
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }
        }
      }

      &-purple {
        &#{$root}_primary {
          background-color: var(--color-purple-50);
          color: var(--color-text-inverse);

          #{$root}__icon {
            fill: var(--color-text-inverse);
          }
        }

        &#{$root}_secondary {
          background-color: var(--color-purple-10);
          color: var(--color-purple-70);

          #{$root}__icon {
            fill: var(--color-purple-70);
          }
        }

        &#{$root}_tertiary {
          background-color: var(--color-purple-05);
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }
        }
      }

      &-yellow {
        &#{$root}_primary {
          background-color: var(--color-yellow-30);
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }
        }

        &#{$root}_secondary {
          background-color: var(--color-yellow-10);
          color: var(--color-yellow-60);

          #{$root}__icon {
            fill: var(--color-yellow-60);
          }
        }

        &#{$root}_tertiary {
          background-color: var(--color-yellow-05);
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }
        }
      }

      &-cyan {
        &#{$root}_primary {
          background-color: var(--color-cyan-50);
          color: var(--color-text-inverse);

          #{$root}__icon {
            fill: var(--color-text-inverse);
          }
        }

        &#{$root}_secondary {
          background-color: var(--color-cyan-20);
          color: var(--color-cyan-70);

          #{$root}__icon {
            fill: var(--color-cyan-70);
          }
        }

        &#{$root}_tertiary {
          background-color: var(--color-cyan-10);
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }
        }
      }

      &-black {
        &#{$root}_primary {
          background-color: var(--color-neutral-90);
          color: var(--color-text-inverse);

          #{$root}__icon {
            fill: var(--color-text-inverse);
          }
        }

        &#{$root}_secondary {
          background-color: var(--color-neutral-60);
          color: var(--color-text-inverse);

          #{$root}__icon {
            fill: var(--color-text-inverse);
          }
        }

        &#{$root}_tertiary {
          background-color: var(--color-neutral-10);
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }
        }
      }

      &-grey {
        &#{$root}_primary {
          background-color: var(--color-slate-40);
          color: var(--color-text-inverse);

          #{$root}__icon {
            fill: var(--color-text-inverse);
          }
        }

        &#{$root}_secondary {
          background-color: var(--color-slate-30);
          color: var(--color-text-inverse);

          #{$root}__icon {
            fill: var(--color-text-inverse);
          }
        }

        &#{$root}_tertiary {
          background-color: var(--color-neutral-10);
          color: var(--color-text-tertiary);

          #{$root}__icon {
            fill: var(--color-text-tertiary);
          }
        }
      }
    }
  }
</style>
